import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useRef } from 'react';
import { throttle } from 'underscore';

import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { useErrorHandling } from '../../../hooks/use-error-handling';
import { useDeleteMarker } from './use-delete-marker';
import { useUpdateMarkerQueue } from './use-update-marker-queue';

/**
 * Returns a list of images for the geodata.
 */
export function useGetMarkersQuery(geodataId: string) {
  const queryId = useMemo(() => `${geodataId}`, [geodataId]);
  const { buildErrorList } = useErrorHandling();

  const { data, refetch, isPending, isRefetching } = useQuery({
    queryKey: ['getGeodataMarkers', queryId],
    queryFn: async () => {
      const response = await SkyMapAxiosServiceFactory.instance
        .createGeodataServiceV0()
        .getMarkers({
          path: {
            geodataId,
          },
        });

      return response.data;
    },
    networkMode: 'always',
    retryOnMount: false,
    retry: false,
    refetchInterval: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 0,
  });

  // TanStack Query v5 recreates refetch on every query-state change. Pin it in a ref
  // so the throttled wrapper and its callers get a stable function identity.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  const stableRefetch = useCallback(() => void refetchRef.current(), []);

  /**
   * Throttle so at most one refetch request is done every 2 seconds.
   */
  const throttledRefetch = useMemo(
    () => throttle(stableRefetch, 2000, { leading: false, trailing: true }),
    [stableRefetch],
  );

  const { queueUpdateMarker: updateMarker, isPendingMarkerUpdates } =
    useUpdateMarkerQueue(geodataId);
  const { deleteMarker } = useDeleteMarker(geodataId);

  const markers = useMemo(() => data ?? [], [data]);
  const errorList = useMemo(() => buildErrorList(), [buildErrorList]);

  return {
    markers,
    refetchMarkers: throttledRefetch,
    updateMarker,
    deleteMarker,
    errorList,
    isPendingMarkers: isPending || isRefetching || isPendingMarkerUpdates,
  };
}
